/**
 * The «Asistente» keyboard shortcut (plan §9.4): Ctrl+K, or ⌘K on Apple
 * devices. Pure, so the chord rules are testable without a DOM.
 */

/** The keyboard event fields the chord rules read. */
export interface IAssistantShortcutEvent {
  readonly key: string;
  readonly code: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly isComposing: boolean;
}

const APPLE_PLATFORM = /mac|iphone|ipad|ipod/i;
const LATIN_LETTER = /^[a-z]$/;

/** Apple keyboards use ⌘ where the others use Ctrl (`navigator.platform`). */
export function isApplePlatform(platform: string | null | undefined): boolean {
  return !!platform && APPLE_PLATFORM.test(platform);
}

/** The chord as the pill shows it: «⌘ K» on Apple devices, «Ctrl K» elsewhere. */
export function assistantShortcutKeys(
  platform: string | null | undefined
): string {
  return isApplePlatform(platform) ? '⌘ K' : 'Ctrl K';
}

/** The chord for `aria-keyshortcuts`. */
export function assistantAriaShortcut(
  platform: string | null | undefined
): string {
  return isApplePlatform(platform) ? 'Meta+K' : 'Control+K';
}

/**
 * Ctrl or ⌘ plus K, without Alt or Shift, outside an IME composition. The K
 * is matched by its character; on layouts that type no Latin letter there
 * (Cyrillic, Arabic, Greek…) by its physical key instead, so the shortcut
 * works in every admin language without stealing another letter's chord.
 */
export function isAssistantShortcut(event: IAssistantShortcutEvent): boolean {
  if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey)
    return false;
  if (event.isComposing) return false;
  const key = event.key.toLowerCase();
  if (key === 'k') return true;
  return !LATIN_LETTER.test(key) && event.code === 'KeyK';
}
